import { useMemo, useState } from 'react';
import { Minus, Plus, Printer, Music2 } from 'lucide-react';
import {
  alignedChordsToChordPro,
  parseChordPro,
  transposeContent,
  type Accidental,
} from '@chordverse/shared';
export function ChordViewer({
  content,
  initialCapo = 0,
}: {
  content: string;
  initialCapo?: number;
}) {
  const [shift, setShift] = useState(0);
  const [capo, setCapo] = useState(initialCapo);
  const [fontSize, setFontSize] = useState(16);
  const [notation, setNotation] = useState<Accidental>('sharp');
  const rendered = useMemo(
    () =>
      parseChordPro(
        transposeContent(alignedChordsToChordPro(content), shift - (capo - initialCapo), notation),
      ),
    [content, shift, capo, initialCapo, notation],
  );
  return (
    <section className="viewer">
      <div className="viewer-toolbar">
        <div>
          <span className="control-label">Transpose</span>
          <div className="stepper">
            <button
              type="button"
              aria-label="Transpose down"
              onClick={() => setShift((v) => v - 1)}
            >
              <Minus />
            </button>
            <strong>{shift > 0 ? `+${shift}` : shift}</strong>
            <button type="button" aria-label="Transpose up" onClick={() => setShift((v) => v + 1)}>
              <Plus />
            </button>
          </div>
        </div>
        <div>
          <span className="control-label">Capo</span>
          <div className="stepper">
            <button
              type="button"
              aria-label="Capo down"
              onClick={() => setCapo((v) => Math.max(0, v - 1))}
            >
              <Minus />
            </button>
            <strong>{capo}</strong>
            <button
              type="button"
              aria-label="Capo up"
              onClick={() => setCapo((v) => Math.min(12, v + 1))}
            >
              <Plus />
            </button>
          </div>
        </div>
        <div>
          <span className="control-label">Notation</span>
          <div className="segmented">
            <button
              type="button"
              className={notation === 'sharp' ? 'active' : ''}
              onClick={() => setNotation('sharp')}
            >
              ♯
            </button>
            <button
              type="button"
              className={notation === 'flat' ? 'active' : ''}
              onClick={() => setNotation('flat')}
            >
              ♭
            </button>
          </div>
        </div>
        <div>
          <span className="control-label">Text size</span>
          <div className="stepper">
            <button
              type="button"
              aria-label="Decrease chord text size"
              onClick={() => setFontSize((value) => Math.max(11, value - 1))}
            >
              <Minus />
            </button>
            <strong>{fontSize}</strong>
            <button
              type="button"
              aria-label="Increase chord text size"
              onClick={() => setFontSize((value) => Math.min(24, value + 1))}
            >
              <Plus />
            </button>
          </div>
        </div>
        <button type="button" className="tool" onClick={() => window.print()}>
          <Printer /> Print
        </button>
      </div>
      <div
        className="chord-paper copy-protected"
        style={{ fontSize: `${fontSize}px` }}
        onCopy={(event) => event.preventDefault()}
        onCut={(event) => event.preventDefault()}
      >
        {rendered.map((line, i) =>
          line.type === 'section' ? (
            <h3 key={i}>{line.text}</h3>
          ) : (
            <div className="chord-line" key={i}>
              {line.segments.map((segment, j) => (
                <span className="lyric-segment" key={j}>
                  {segment.chord && <b>{segment.chord}</b>}
                  <span>{segment.lyric || '\u00a0'}</span>
                </span>
              ))}
            </div>
          ),
        )}
        {!content && (
          <div className="empty">
            <Music2 />
            <p>No chord content yet.</p>
          </div>
        )}
      </div>
    </section>
  );
}
